import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { createConference, updateConference, deleteConference } from '../../utils/api';

export const ConferencesManager = ({ conferences, onUpdate, onExport }) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', date: '', location: '', mode: 'physical', fee: '', status: 'upcoming'
  });

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ title: '', description: '', date: '', location: '', mode: 'physical', fee: '', status: 'upcoming' });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title || '', description: item.description || '', date: item.date || '', location: item.location || '', mode: item.mode || 'physical', fee: item.fee || '', status: item.status || 'upcoming' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateConference(editingItem.id, formData);
        toast({ title: 'Success', description: 'Conference updated' });
      } else {
        await createConference(formData);
        toast({ title: 'Success', description: 'Conference created' });
      }
      setDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this conference?')) return;
    try {
      await deleteConference(id);
      toast({ title: 'Success', description: 'Conference deleted' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Manage Conferences ({conferences.length})</CardTitle>
          <div className="flex gap-2">
            <Button onClick={onExport}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={handleAdd}><Plus className="w-4 h-4 mr-2" />Add Conference</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Fee</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {conferences.map((conf) => (
              <TableRow key={conf.id}>
                <TableCell>{conf.title}</TableCell>
                <TableCell>{conf.date}</TableCell>
                <TableCell>{conf.location}</TableCell>
                <TableCell>₦{conf.fee?.toLocaleString()}</TableCell>
                <TableCell><span className={`px-2 py-1 rounded text-xs ${conf.status === 'upcoming' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{conf.status}</span></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(conf)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(conf.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Conference' : 'Add Conference'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Title *</Label><Input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} /></div>
              <div><Label>Description *</Label><Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date *</Label><Input required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} /></div>
                <div><Label>Location *</Label><Input required value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Fee (₦) *</Label><Input required type="number" value={formData.fee} onChange={(e) => setFormData({ ...formData, fee: e.target.value })} /></div>
                <div><Label>Status *</Label><select required className="w-full border rounded px-3 py-2" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}><option value="upcoming">Upcoming</option><option value="ongoing">Ongoing</option><option value="completed">Completed</option></select></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};